import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { PhotoIntervention } from '../../Services/intervention-api';
import {
  InterventionPhotos as PhotosApi,
  ReferencePhotosIntervention,
} from '../../Services/intervention-photos';
import { messageErreurApi } from '../../Services/api-error';

/** État indépendant de chaque fichier : attente du blob, aperçu disponible ou erreur locale. */
type ApercuPhoto = { photo: PhotoIntervention; url: string | null; erreur: string };

/** Galerie de blobs authentifiés ; les balises img ne pointent jamais directement vers l’API. */
@Component({
  selector: 'app-intervention-photos',
  templateUrl: './intervention-photos.html',
  styleUrl: './intervention-photos.css',
})
export class InterventionPhotos {
  readonly intervention = input<ReferencePhotosIntervention | null>(null);
  /** En modification, la lecture du bon fournit déjà les métadonnées. */
  readonly photos = input<readonly PhotoIntervention[] | null>(null);
  private readonly api = inject(PhotosApi);
  private readonly revision = signal(0);
  readonly sessionFermee = signal(false);
  readonly chargement = signal(false);
  readonly erreur = signal('');
  readonly apercus = signal<ApercuPhoto[]>([]);
  readonly peutReessayer = computed(
    () => !!this.erreur() || this.apercus().some((p) => !!p.erreur),
  );

  /** Relance les lectures au changement de contexte et libère les ressources du précédent affichage. */
  constructor() {
    this.api.sessionChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.sessionFermee.set(true));
    effect((onCleanup) => {
      const intervention = this.intervention();
      const photos = this.photos();
      const sessionFermee = this.sessionFermee();
      this.revision();
      const requetes = new Subscription();
      const urls = new Set<string>();
      // Changement d’entrée, nouvelle tentative, fin de session ou destruction : annuler les
      // requêtes et révoquer chaque URL locale pour éviter les images obsolètes et les fuites mémoire.
      onCleanup(() => {
        requetes.unsubscribe();
        urls.forEach((url) => URL.revokeObjectURL(url));
      });
      this.apercus.set([]);
      this.erreur.set('');
      this.chargement.set(false);
      if (sessionFermee) return;

      const afficher = (liste: readonly PhotoIntervention[]) =>
        this.chargerApercus(liste, requetes, urls);
      // Une liste fournie, même vide, évite de relire les métadonnées déjà obtenues en édition.
      if (photos !== null) afficher(photos);
      else if (intervention) {
        this.chargement.set(true);
        requetes.add(
          this.api.lister(intervention).subscribe({
            next: afficher,
            error: (erreur: unknown) => {
              this.chargement.set(false);
              this.erreur.set(
                messageErreurApi(erreur, 'Impossible de charger la liste des photos.'),
              );
            },
          }),
        );
      }
    });
  }

  /** Invalide l’affichage courant pour relancer les téléchargements sans conserver les blobs. */
  reessayer(): void {
    this.revision.update((revision) => revision + 1);
  }

  /** Signale un fichier dont le type est accepté mais que le navigateur ne peut pas décoder. */
  imageIllisible(id: number): void {
    this.actualiserApercu(id, { erreur: 'Cette image est illisible.' });
  }

  /** Télécharge chaque photo indépendamment ; les ressources restent rattachées à l’effet courant. */
  private chargerApercus(
    photos: readonly PhotoIntervention[],
    requetes: Subscription,
    urls: Set<string>,
  ): void {
    this.chargement.set(false);
    this.apercus.set(photos.map((photo) => ({ photo, url: null, erreur: '' })));
    for (const photo of photos) {
      requetes.add(
        this.api.charger(photo).subscribe({
          next: (blob) => {
            const url = URL.createObjectURL(blob);
            urls.add(url);
            this.actualiserApercu(photo.id, { url });
          },
          error: (erreur: unknown) => {
            this.actualiserApercu(photo.id, {
              erreur: messageErreurApi(erreur, 'Impossible de charger cette photo.'),
            });
          },
        }),
      );
    }
  }

  /** Met à jour une seule image sans remplacer les métadonnées ni les autres téléchargements. */
  private actualiserApercu(
    id: number,
    valeurs: Partial<Pick<ApercuPhoto, 'url' | 'erreur'>>,
  ): void {
    this.apercus.update((items) =>
      items.map((item) => (item.photo.id === id ? { ...item, ...valeurs } : item)),
    );
  }
}
